/**
 * User model representing authenticated user data
 * Based on AWS Amplify user attributes
 */
export interface User {
  sub?: string; // Cognito user ID
  email?: string;
  email_verified?: string;
  name?: string;
  family_name?: string;
  given_name?: string;
  picture?: string;
  locale?: string;
  // Add other custom attributes as needed
  [key: string]: string | undefined;
}

/**
 * Extended user data with backend sync information
 */
export interface UserData extends User {
  backendUserId?: string;
  syncedAt?: string;
}

/**
 * Authentication state for NgRx store
 */
export interface AuthState {
  // User authentication status
  isAuthenticated: boolean;
  
  // User data from Amplify and backend
  user: UserData | null;
  
  // Loading states for different operations
  loading: {
    login: boolean;
    logout: boolean;
    registration: boolean;
    userSync: boolean;
    tokenRefresh: boolean;
    passwordChange: boolean;
    emailConfirmation: boolean;
    resendCode: boolean;
    passwordReset: boolean;
    confirmPasswordReset: boolean;
  };
  
  // Error states for different operations
  error: {
    login: string | null;
    logout: string | null;
    registration: string | null;
    userSync: string | null;
    tokenRefresh: string | null;
    passwordChange: string | null;
    emailConfirmation: string | null;
    resendCode: string | null;
    passwordReset: string | null;
    confirmPasswordReset: string | null;
  };
  
  // Auth flow status flags
  initialAuthCheckCompleted: boolean;
  userBackendSyncCompleted: boolean;
  
  // Last processed user to prevent duplicate processing
  lastProcessedUserId: string | null;
  
  // Amplify OAuth processing status
  processingOAuth: boolean;
}

/**
 * Initial state for auth feature
 */
export const initialAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: {
    login: false,
    logout: false,
    registration: false,
    userSync: false,
    tokenRefresh: false,
    passwordChange: false,
    emailConfirmation: false,
    resendCode: false,
    passwordReset: false,
    confirmPasswordReset: false,
  },
  error: {
    login: null,
    logout: null,
    registration: null,
    userSync: null,
    tokenRefresh: null,
    passwordChange: null,
    emailConfirmation: null,
    resendCode: null,
    passwordReset: null,
    confirmPasswordReset: null,
  },
  initialAuthCheckCompleted: false,
  userBackendSyncCompleted: false,
  lastProcessedUserId: null,
  processingOAuth: false,
};

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Registration data
 */
export interface RegistrationData {
  email: string;
  password: string;
}

/**
 * OAuth provider types
 */
export type OAuthProvider = 'Google' | 'Facebook' | 'Apple';

/**
 * Auth error details
 */
export interface AuthError {
  message: string;
  code?: string;
  name?: string;
}
